type RootType = Record<string, unknown>;

class ChangeTracker {
  private root: RootType;
  private properties: string[];
  private hashes: Record<string, string> = {};

  constructor(root: RootType, properties: string[]) {
    this.root = root;
    this.properties = properties;
    this.reset();
  }

  reset() {
    this.properties.forEach((property) => {
      this.hashes[property] = this.getHash(property);
    });
  }

  getIfChanged<T>(property: string): T | undefined {
    return this.isChanged(property) ? (this.root[property] as T) : undefined;
  }

  appendIfChanged<T>(item: RootType, property: string) {
    const data = this.getIfChanged<T>(property);
    if (data !== undefined) {
      item[property] = data;
    }
  }

  isChanged(property: string) {
    return this.hashes[property] !== this.getHash(property);
  }

  hasAnyChange() {
    return this.properties.some((property) => this.isChanged(property));
  }

  isStale(freshData: RootType) {
    const freshHashes: Record<string, string> = {};

    this.properties.forEach((property) => {
      freshHashes[property] = this.getHash(property, freshData);
    });

    const initialHash = JSON.stringify(this.hashes);
    const freshHash = JSON.stringify(freshHashes);

    return !(initialHash === freshHash);
  }

  private getHash(property: string, defaultRoot?: RootType) {
    const data = (defaultRoot || this.root)[property];
    const normalized = this.normalize(data);
    return JSON.stringify(normalized);
  }

  private normalize(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value
        .map((v) => this.normalize(v))
        .toSorted((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    }

    if (value && typeof value === "object") {
      const obj = value as RootType;
      return Object.keys(obj)
        .sort()
        .reduce((acc, key) => {
          acc[key] = this.normalize(obj[key]);
          return acc;
        }, {} as RootType);
    }

    return value;
  }
}

export default ChangeTracker;
